// Forma para llenar ubicaciones (site)

import React, { useEffect, useState } from 'react';
import { Button, Card, CardContent, CardActions, Grid, Typography, TextField, MenuItem } from "@mui/material";
import {
  FIELD_LABELS,
  nameValidator,
  requiredValidator,
  zipCodeValidator,
  validateField,
  streetNameValidator,
  latLongValidator
} from '../validators';
import { MEXICAN_STATES } from '../states'
import { apiFetch, apiFetchJson } from '../fetchWrapper';
import { getAddressInfoForZipCode } from '../sepomex.js'


export default function SiteForm({ site, clientId, onCancel, onSave }) {

  const initialForm = {
    siteName: '',
    street: '',
    neighborhood: '',
    municipality: '',
    city: '',
    zipCode: '',
    state: '',
    country: 'Mexico',
  };

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [error, setError] = useState(null);

  const hasErrors = Object.keys(errors).length > 0;
  const requiredFieldsFilled = form.siteName && form.street && form.city && form.municipality && form.zipCode && form.state;

  useEffect(() => {
    if (site) {
      setNeighborhoods(site.address?.neighborhood ? [site.address.neighborhood] : []);
      setForm({
        siteName: site.name || '',
        street: site.address?.street || '',
        neighborhood: site.address?.neighborhood || '',
        municipality: site.address?.municipality || '',
        city: site.address?.city || '',
        zipCode: site.address?.zipCode || '',
        state: site.address?.state || '',
        country: site.address?.country || 'Mexico',
        latitude: site.latitude || '',
        longitude: site.longitude || '',
        elementsNumber: site.elementsNumber || 0,
      });
    } else {
      setForm(initialForm);
    }
  }, [site]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      name: form.siteName,
      address: {
        id: site?.address?.id || null,
        street: form.street,
        neighborhood: form.neighborhood,
        municipality: form.municipality,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        country: form.country || 'Mexico',
      },
      latitude: form.latitude || null,
      longitude: form.longitude || null,
      elementsNumber: form.elementsNumber || 0,
      client: { id: clientId },
    };

    const url = site ? `/api/sites/${site.id}` : `/api/clients/${clientId}/sites`;
    const method = site ? 'PUT' : 'POST';

    try {
      const savedSite = await apiFetchJson(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (!savedSite) throw new Error("Failed to save site");
      setForm(initialForm);
      onSave(savedSite);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save site');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors(prev => {
      const next = { ...prev };
      let error = null;

      if (name === 'siteName' || name === 'street') {
        error = validateField(value, [
          requiredValidator,
          streetNameValidator
        ]);
      }

      if (name === 'municipality' || name === 'city') {
        error = validateField(value, [
          requiredValidator,
          (v) => nameValidator(v, FIELD_LABELS[name])
        ]);
      }

      if (name === 'zipCode') {
        error = validateField(value, [
          requiredValidator,
          zipCodeValidator
        ]);
      }

      if (name === 'latitude' || name === 'longitude') {
        error = validateField(value, [
          latLongValidator
        ]);
      }

      if (name === 'zipCode' && !error && value.length === 5) {
        // solo llama a SEPOMEX si el CP es valido y completo (5 digitos)
        setTimeout(() => {
          getAddressInfoForZipCode(value).then((data) => {
            if (data) {
              if (data.city) {
                setForm((prev) => ({ ...prev, city: data.city }));
              }
              if (data.state) {
                setForm((prev) => ({ ...prev, state: data.state }));
              }
              if (data.municipality) {
                setForm((prev) => ({ ...prev, municipality: data.municipality }));
              }
              if (data.neighborhoods.length > 0) {
                setNeighborhoods(data.neighborhoods);
                setForm((prev) => ({ ...prev, neighborhood: data.neighborhoods[0] }));
              }
            }
          });
        });
      }

      if (error) {
        next[name] = error;
      } else {
        delete next[name];
      }

      return next;
    });
  };

  const handleCancel = () => {
    setForm(initialForm);
    onCancel();
  };

  // Geocoding -- busca la ubicacion (coordenadas) de una direccion
  const handleGeoLocate = async () => {
    const address = `${form.street}, ${form.city}, ${form.state}, ${form.zipCode}, ${form.country}`;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
      const data = await res.json();
      if (data.length > 0) {
        setForm(prev => ({
          ...prev,
          latitude: data[0].lat,
          longitude: data[0].lon
        }));
      } else {
        alert("No se encontraron coordenadas para esta dirección");
      }
    } catch (err) {
      console.error(err);
      alert("Error al buscar la ubicación");
    }
  };

  const enableGeoLocate = form.street && form.city && form.state && form.zipCode && form.country;

  return (
    <Card
      sx={{
        maxWidth: '70%',
        margin: '1rem auto',
        padding: 1,
        backgroundColor: '#f9f9f9',
      }}
    >
      <CardContent sx={{ pt: 2 }}
      >
        <Typography variant="subtitle1"
          sx={{
            mb: 2,
            fontSize: '16px',
            fontWeight: 600
          }}>
          Registro nueva ubicación
        </Typography>

        <form onSubmit={handleSubmit}>

          <Grid container spacing={2}>

            <Grid size={{ xs: 12, md: 10, lg: 10 }}>
              <TextField
                label="Nombre ubicación"
                name="siteName"
                error={!!errors.siteName}
                helperText={errors.siteName}
                value={form.siteName}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2, lg: 2 }}>
              <TextField
                fullWidth
                label="Numero de elementos"
                name="elementsNumber"
                type='number'
                value={form.elementsNumber}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setForm((prev) => ({
                      ...prev,
                      elementsNumber: '',
                    }));  
                    return;
                  }

                  let value = Number(raw);

                  if (value < 1) value = 1;
                  if (value > 100) value = 100;

                  setForm((prev) => ({
                    ...prev,
                    elementsNumber: value,
                  }));
                }}
              >
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                label="Calle y número"
                name="street"
                error={!!errors.street}
                helperText={errors.street}
                value={form.street}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                label="Código Postal"
                name="zipCode"
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                value={form.zipCode}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 5 } }}
                fullWidth
                required
              />
            </Grid>

            {/* <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                select
                label="Colonia"
                name="neighborhood"
                value={form.neighborhood}
                onChange={handleChange}
                error={!!errors.neighborhood}
                helperText={errors.neighborhood}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              >
                {neighborhoods.map((colonia) => (
                  <MenuItem key={colonia} value={colonia}>
                    {colonia}
                  </MenuItem>
                ))}
              </TextField>
            </Grid> */}



            <Grid size={{ xs: 12, md: 6, lg: 4}}>
              {neighborhoods.length > 0 ? (
                <TextField
                  select
                  label="Colonia"
                  name="neighborhood"
                  value={form.neighborhood}
                  onChange={handleChange}
                  error={!!errors.neighborhood}
                  helperText={errors.neighborhood}
                  fullWidth
                  required
                >
                  <MenuItem value="">
                    <em>Seleccionar colonia</em>
                  </MenuItem>

                  {neighborhoods.map((colonia) => (
                    <MenuItem key={colonia} value={colonia}>
                      {colonia}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  label="Colonia"
                  name="neighborhood"
                  value={form.neighborhood}
                  onChange={handleChange}
                  error={!!errors.neighborhood}
                  helperText={
                    errors.neighborhood ||
                    'Escribe la colonia manualmente'
                  }
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                label="Ciudad"
                name="city"
                error={!!errors.city}
                helperText={errors.city}
                value={form.city}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                label="Alcaldía/Municipio"
                name="municipality"
                error={!!errors.municipality}
                helperText={errors.municipality}
                value={form.municipality}
                onChange={handleChange}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { maxLength: 100 }
                }}
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                select
                label="Estado"
                name="state"
                value={form.state}
                onChange={handleChange}
                fullWidth
                required
              >
                {MEXICAN_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.name}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                label="Latitud"
                name="latitude"
                error={!!errors.latitude}
                helperText={errors.latitude}
                value={form.latitude}
                onChange={handleChange}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                label="Longitud"
                name="longitude"
                value={form.longitude}
                error={!!errors.longitude}
                helperText={errors.longitude}
                onChange={handleChange}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
            </Grid>

            <CardActions
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1
              }}
            >

              <Button
                type="button"
                variant="contained"
                color="error"
                onClick={handleCancel}
                disabled={saving}>
                Cancelar
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={handleGeoLocate}
                disabled={!enableGeoLocate}
                color="secondary">
                Geolocalizar
              </Button>

              <Button
                sx={{
                  backgroundColor: "#3f74b1",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#364f6b"
                  }
                }}
                type="submit"
                disabled={saving || hasErrors || !requiredFieldsFilled}
                variant="contained"
                color="primary">
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </CardActions>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
}

